import React from 'react';
import { PlaybookTransformItem, PlaybookTransformType, PlaybookExpression, PlaybookSpreading } from '@/types/playbook';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { UnifiedSchema, EventSchema } from '@/types/schema';
import { EditorSelection } from './PlaybookEditorTab';
import { PlaybookEventConfig } from '@/types/playbook';
import { getTypeColorClass } from '@/utils/schemaUtils';
import PlaybookTransformTableRow from './transforms-list/PlaybookTransformTableRow';

interface PlaybookTransformTableProps {
  transforms: PlaybookTransformItem[];
  selectedTransform: PlaybookTransformItem | null;
  isEditable: boolean;
  onTransformSelect: (transformId: string) => void;
  onDeleteTransform: (transformId: string) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  onSelectionChange: (selection: EditorSelection) => void;
  inputSchema: UnifiedSchema | null; 
  outputSchema: UnifiedSchema | null; 
  allEvents: EventSchema[]; 
  uiEventConfigs: PlaybookEventConfig[];
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  sortBy: 'inputKey' | 'transformType' | 'outputKey' | 'mappingType' | 'events';
  sortDirection: 'asc' | 'desc';
  handleSort: (column: 'inputKey' | 'transformType' | 'outputKey' | 'mappingType' | 'events') => void;
  getTransformDisplayName: (type: PlaybookTransformType) => string;
  getTransformDetailDisplay: (transform: PlaybookTransformItem) => string;
  getMappingTypeDisplay: (transform: PlaybookTransformItem) => JSX.Element | null;
  getTypeIcon: (dataType?: string) => JSX.Element;
  truncatePath: (path: string, maxLength?: number) => string;
  isPropertyPairApplicableToAllEvents: (inputPath: string | undefined, outputPath: string | undefined, allEvents: EventSchema[]) => boolean;
}

const PlaybookTransformTable: React.FC<PlaybookTransformTableProps> = ({
  transforms,
  selectedTransform,
  isEditable,
  onTransformSelect,
  onDeleteTransform,
  onUpdateTransform,
  onSelectionChange,
  inputSchema, 
  outputSchema, 
  allEvents,
  uiEventConfigs, 
  allUiTransforms, 
  allowUndefinedTransforms, 
  sortBy,
  sortDirection,
  handleSort,
  getTransformDisplayName,
  getTransformDetailDisplay,
  getMappingTypeDisplay,
  getTypeIcon,
  truncatePath,
  isPropertyPairApplicableToAllEvents, // Destructure this prop
}) => {
  const getSortIcon = (key: typeof sortBy) => {
    if (sortBy !== key) {
      return null;
    }
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="w-4 h-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline-block ml-1" />
    );
  };

  const getOutputDisplay = (transform: PlaybookTransformItem) => {
    if (transform.outputPath) {
      return (
        <>
          {transform.outputType && (
            <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(transform.outputType)}`}>
              {getTypeIcon(transform.outputType)}
            </span>
          )}
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={transform.outputPath}>
            {truncatePath(transform.outputPath)}
          </span>
        </>
      );
    } else if (transform.type === 'spreading') {
      const spreadingContent = transform.content as PlaybookSpreading;
      return (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {spreadingContent.inferredOutputProperties?.length 
            ? `${spreadingContent.inferredOutputProperties.length} properties`
            : 'Series of properties'}
        </span>
      );
    } else if (transform.type === 'expression') {
      const expressionContent = transform.content as PlaybookExpression;
      return expressionContent.declaredOutputProperties?.length === 1 ? (
        <>
          {expressionContent.declaredOutputProperties![0].dataType && (
            <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(expressionContent.declaredOutputProperties![0].dataType)}`}>
              {getTypeIcon(expressionContent.declaredOutputProperties![0].dataType)}
            </span>
          )}
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate" title={expressionContent.declaredOutputProperties![0].path}>
            {truncatePath(expressionContent.declaredOutputProperties![0].path)}
          </span>
        </>
      ) : (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {expressionContent.declaredOutputProperties?.length 
            ? `${expressionContent.declaredOutputProperties.length} outputs`
            : 'Dynamic outputs'}
        </span>
      );
    } else if (transform.type === 'filter') {
      return (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Filters events
        </span>
      );
    } else if (transform.type === 'mapping' && !transform.outputPath) {
      return (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          No output
        </span>
      );
    }
    return null;
  };

  return (
    <div className="overflow-x-auto rounded-lg border transition-colors relative group border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[25%] cursor-pointer"
              onClick={() => handleSort('inputKey')}
            >
              Input Key {getSortIcon('inputKey')}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[15%] cursor-pointer"
              onClick={() => handleSort('transformType')}
            >
              Transform {getSortIcon('transformType')}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[25%] cursor-pointer"
              onClick={() => handleSort('outputKey')}
            >
              Output Key {getSortIcon('outputKey')}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[15%] cursor-pointer"
              onClick={() => handleSort('mappingType')}
            >
              Matching {getSortIcon('mappingType')}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[10%] cursor-pointer"
              onClick={() => handleSort('events')}
            >
              Events {getSortIcon('events')}
            </th>
            <th scope="col" className="relative px-6 py-3 w-[10%]">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {transforms.map((transform) => (
            <PlaybookTransformTableRow
              key={transform.id}
              transform={transform}
              isSelected={selectedTransform?.id === transform.id}
              onTransformSelect={onTransformSelect}
              onDeleteTransform={onDeleteTransform}
              onUpdateTransform={onUpdateTransform}
              onSelectionChange={onSelectionChange}
              inputSchema={inputSchema}
              outputSchema={outputSchema}
              allEvents={allEvents}
              uiEventConfigs={uiEventConfigs}
              allUiTransforms={allUiTransforms}
              allowUndefinedTransforms={allowUndefinedTransforms}
              isEditable={isEditable}
              getTransformDisplayName={getTransformDisplayName}
              getTransformDetailDisplay={getTransformDetailDisplay}
              getMappingTypeDisplay={getMappingTypeDisplay}
              getTypeIcon={getTypeIcon}
              truncatePath={truncatePath}
              isPropertyPairApplicableToAllEvents={(inputPath, outputPath) => isPropertyPairApplicableToAllEvents(inputPath, outputPath, allEvents)}
              getOutputDisplay={getOutputDisplay}
            />
          ))}
        </tbody>
      </table>
      {transforms.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No transforms match your current filters.
        </p>
      )}
    </div>
  );
};

export default PlaybookTransformTable;
